// ─────────────────────────────────────────────
// KLOTHIQ — useDeferredVisible
// FEAT-LOADER-001: appearance-delay + minimum-display-duration for
// loading indicators. `BrandLoader` calls this internally so every
// existing call site (LoadingState's 20 usages, plus the boot loader)
// gets debounced show/hide with zero changes to how they set their own
// `isLoading` boolean — see spec FR-007.
// ─────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';

/**
 * Debounces a boolean "is loading" signal into a "should the loader be
 * visible right now" signal:
 *   - Never becomes visible if `active` flips back to false within `delay`.
 *   - Once visible, stays visible at least `minDuration`, even if `active`
 *     goes false sooner — avoids a one-frame flash/flicker.
 *
 * Known limitation: the minimum-duration guarantee only holds while this
 * hook's component instance stays mounted. LoadingState's call sites
 * conditionally MOUNT/UNMOUNT it (`{isLoading && <LoadingState />}`)
 * rather than keeping it mounted and toggling a prop — so if the parent
 * unmounts the whole subtree the instant its own isLoading flips false,
 * this hook's pending "stay visible a bit longer" timer never gets to
 * run; the component is just gone. The appearance-delay half is
 * unaffected (an unmount before `delay` elapses correctly cancels the
 * show-timer either way). In practice this only shortens the minimum
 * hold for loads that finish inside the 200–600ms window right after
 * becoming visible — still strictly better than no debounce at all, just
 * not a hard guarantee for those call sites. See spec UNK-004.
 */
export function useDeferredVisible(active: boolean, delay = 200, minDuration = 400): boolean {
  const [visible, setVisible] = useState(false);
  const visibleRef = useRef(false);
  const shownAtRef = useRef(0);
  const showTimer = useRef<ReturnType<typeof setTimeout>>();
  const hideTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (active) {
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
        hideTimer.current = undefined;
      }
      if (!visibleRef.current && !showTimer.current) {
        showTimer.current = setTimeout(() => {
          showTimer.current = undefined;
          shownAtRef.current = Date.now();
          visibleRef.current = true;
          setVisible(true);
        }, delay);
      }
    } else {
      if (showTimer.current) {
        clearTimeout(showTimer.current);
        showTimer.current = undefined;
      }
      if (visibleRef.current && !hideTimer.current) {
        const remaining = Math.max(0, minDuration - (Date.now() - shownAtRef.current));
        hideTimer.current = setTimeout(() => {
          hideTimer.current = undefined;
          visibleRef.current = false;
          setVisible(false);
        }, remaining);
      }
    }
  }, [active, delay, minDuration]);

  // True-unmount cleanup only — intentionally separate from the effect
  // above so a StrictMode dev double-invoke doesn't clear a timer the
  // other effect run still needs (spec EC-003).
  useEffect(() => {
    return () => {
      if (showTimer.current) clearTimeout(showTimer.current);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  return visible;
}
